/**
 * The flight dashboard feature (dashboard spec §1, §2, §5.8–5.10; card D1, D-617). Tab (the `dashboard` binding,
 * D2) opens the dashboard over flight; by default it holds the sim (`HOLD.dashboard`), with Settings › Interface ›
 * *Dashboard time* = Keep running it only counts as a modal. Keys are taken by a **capture-phase** keydown listener
 * on `window` so the closing press re-locks the pointer inside its own gesture (the frame-polled path runs a rAF
 * later); see `controller.ts` for the open/close/Esc rules. The shell is React, mounted once and hidden while closed.
 *
 * Closed, `frame` returns at once and nothing else runs (§5.10). The pause menu's Dashboard row opens it too (W3 M4).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature, type Settings } from '../../app/feature'
import { HOLD } from '../../career/runtime'
import { deepLinkFor, drainPressed } from '../../input/devices'
import { loadDashboardConfig, type DashboardConfig } from '../../ui/dashboard/config'
import { createDashNavPort } from '../../ui/dashboard/focus'
import { createDashView, type DashDomView } from '../../ui/dashboard/host'
import { setDashboardHost, type DashCommandPort, type DashContext, type DashTarget } from '../../ui/dashboard/registry'
import type { CourseTarget } from '../../ui/dashboard/tabs/jobs/model'
import { installJobsNavSections } from '../../ui/dashboard/tabs/nav/install'
import { overviewData } from '../../ui/dashboard/tabs/overview/model'
import { createOverviewSource } from '../../ui/dashboard/tabs/overview/source'
import { createDashController, type DashController, type DashTimeMode } from './controller'

/** After the pause menu (700), before the station screens (710). */
const ORDER = FEATURE_ORDER.menu + 5

/**
 * Settings › Interface › *Dashboard time* (spec §5.6). TODO(F2): card F2 adds `dashboardTime: 'pause' |
 * 'keepRunning'` to `Settings` (default 'pause', DASHBOARD_TIME_MODES in app/feature.ts; helper `dashboardHoldsSim` in
 * settings/apply.ts); on merge read `settings.dashboardTime` directly. Until it lands the key is absent: Pause.
 */
export function dashboardTimeMode(settings: Settings): DashTimeMode {
  const v = (settings as Settings & { readonly dashboardTime?: unknown }).dashboardTime
  return v === 'keepRunning' ? 'keepRunning' : 'pause'
}

/**
 * The keys bound to the `dashboard` action (D2: `dashboard: ["Tab"]` in bindings.json; it also moved
 * `toolCycle` to Backquote); `dashboard.json openKey` stands in only when the binding is missing.
 */
export function dashboardKeys(keyboard: object, fallback: string): readonly string[] {
  const bound = (keyboard as Readonly<Record<string, unknown>>)['dashboard']
  if (Array.isArray(bound) && bound.length > 0 && bound.every((k): k is string => typeof k === 'string')) return bound
  return [fallback]
}

/**
 * The deep-link resolver (spec §1.2): N/K/U/L (`bindings.json` wirePanel, charterBoard, fleetScreen, logs) → their
 * `dashboard.json deepLinkTargets` landing; null for any other key, and always while `deepLinks` is off.
 */
export function dashDeepLinks(bindings: Parameters<typeof deepLinkFor>[0], cfg: Pick<DashboardConfig, 'deepLinks' | 'deepLinkTargets'>): (code: string) => DashTarget | null {
  return (code: string): DashTarget | null => {
    const action = deepLinkFor(bindings, code, cfg.deepLinks)
    return action === null ? null : cfg.deepLinkTargets[action]
  }
}

export interface DashboardFeature extends GameFeature {
  readonly controller: DashController | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export function createDashboardFeature(): DashboardFeature {
  let controller: DashController | null = null
  let view: DashDomView | null = null
  const offs: (() => void)[] = []

  const onKeyDown = (e: KeyboardEvent): void => {
    if (controller === null || !controller.keydown(e.code, e.repeat)) return
    e.preventDefault()
    e.stopPropagation()
  }

  const feature: DashboardFeature = {
    name: 'dashboard',
    order: ORDER,
    get controller(): DashController | null {
      return controller
    },

    init(ctx: GameContext): void {
      const cfg = loadDashboardConfig()
      const port: DashCommandPort = {
        open: (at: DashTarget) => controller?.open(at),
        close: (relock: boolean) => controller?.close(relock),
      }
      const nav = createDashNavPort()
      const v = createDashView({
        anchor: ctx.overlayRoot,
        cfg,
        port,
        nav,
        onTab: (i) => controller?.selectTab(i),
        onSection: (i) => controller?.selectSection(i),
      })
      view = v
      // W4 C1: the Overview cards and header readouts, read with each snapshot (refreshHz, only while open).
      const overview = createOverviewSource(ctx)
      offs.push(() => overview.dispose())
      const c = createDashController({
        ui: ctx.ui,
        holds: ctx.holds,
        holdId: HOLD.dashboard,
        view: v,
        keys: dashboardKeys(ctx.data.bindings.keyboard, cfg.openKey),
        timeMode: () => dashboardTimeMode(ctx.settings),
        dashCtx: (): DashContext => ({ mode: ctx.mode, docked: ctx.holds.has(HOLD.docked), career: ctx.reach !== null && ctx.mode === 'career' }),
        refreshHz: cfg.refreshHz,
        badgeHz: cfg.badgeHz,
        nav,
        deepLink: dashDeepLinks(ctx.data.bindings, cfg),
        overview: () => {
          const r = overview.read()
          return { ...overviewData(r.input, r.alerts), course: (t: CourseTarget) => overview.course(t) }
        },
      })
      controller = c
      window.addEventListener('keydown', onKeyDown, { capture: true })
      offs.push(
        () => window.removeEventListener('keydown', onKeyDown, { capture: true }),
        ctx.bus.on('firstGesture', () => c.setPaused(false)),
        ctx.bus.on('pause', () => c.setPaused(true)),
        ctx.bus.on('resume', () => c.setPaused(false)),
        ctx.bus.on('settings', () => c.applyTimeMode()),
        setDashboardHost({
          open: (at?: DashTarget) => c.open(at),
          close: (relock: boolean) => c.close(relock),
          get isOpen(): boolean {
            return c.isOpen
          },
        }),
      )
      // W4b C3: Jobs and Nav sections (career in the Reach only; they read the game lazily while open).
      if (ctx.reach !== null) offs.push(installJobsNavSections(ctx))
      // The game starts paused behind the start overlay (and the title screen holds): no keys until it runs.
      c.setPaused(true)
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      // The pad's View tap (D2's `dashboard` edge; the keyboard Tab is the capture listener, never polled).
      if (drainPressed(ctx.input, 'dashboard')) controller?.padToggle()
      controller?.frame(f.dt)
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
      view?.dispose()
      view = null
      controller = null
    },
  }
  return feature
}

export default createDashboardFeature()
